import { useCallback, useLayoutEffect, useRef, useState } from 'react'

import UIIcon, { UIIconType } from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'

export type ButtonProps = {
  className?: string
  onClick?: () => void
  onAppeared?: (element: HTMLElement) => void
  type?: 'button' | 'submit' | 'reset'
  is?: 'button' | 'label' | 'a'

  disabled?: boolean
  active?: boolean

  appear?: boolean
  appearDelay?: number

  ariaCurrent?: boolean
  ariaLabel?: string

  classNameLabel?: string
  label?: string
  labelPosition?: 'bottom' | 'bottom-right' | 'bottom-left' | 'right' | 'left'

  target?: string
  href?: string

  size?: 'small' | 'large'

  icon: UIIconType
  style?: string
}

export default function Button({
  className,

  onClick,
  onAppeared,

  disabled,

  type = undefined,
  is = 'button',
  active,

  size = 'small',

  appear = false,
  appearDelay = 0,
  ariaCurrent = undefined,
  ariaLabel = undefined,

  classNameLabel,
  label = null,
  labelPosition = 'right',

  target = null,
  href = null,

  icon,
  style = '',
}: ButtonProps) {
  const Component = is
  const buttonRef = useRef(null)

  const { gsap } = useGSAP()

  const props = is === 'a' ? { href, target } : {}

  const _onClick = useCallback(() => {
    onClick?.()
  }, [onClick])

  const [hasAppeared, setHasAppeared] = useState(false)
  const background = useRef<HTMLElement>(null)
  const inner = useRef<HTMLElement>(null)

  const classNameLabelPosition = (() => {
    switch (labelPosition) {
      case 'bottom-right':
        return 'right-0 top-full mt-8 group-hover:-translate-y-3'
      case 'bottom-left':
        return 'left-0 top-full mt-8 group-hover:-translate-y-3'
      case 'left':
        return 'right-full top-1/2 -translate-y-1/2 mr-8 group-hover:translate-x-3'
      case 'right':
        return 'left-full top-1/2 -translate-y-1/2 ml-8 group-hover:-translate-x-3'
      default:
        return 'left-1/2 -translate-x-1/2 top-full mt-8 group-hover:-translate-y-3'
    }
  })()

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (!appear) {
        setHasAppeared(true)
      } else {
        gsap
          .timeline({
            delay: appearDelay,
            onComplete: () => {
              setHasAppeared(true)
              onAppeared?.(buttonRef.current)
            },
          })
          .add(() => setHasAppeared(true), 0)
          .fromTo(
            background.current,
            {
              clipPath: 'circle(0% at 50% 110%)',
            },
            {
              clipPath: 'circle(80% at 50% 100%)',
              duration: 0.7,
              ease: 'power4.inOut',
            },
            0
          )
          .fromTo(
            inner.current,
            {
              yPercent: 100,
            },
            {
              yPercent: 0,
              duration: 0.7,
              ease: 'power4.inOut',
            },
            0
          )
      }
    })

    return () => ctx.kill()
  }, [appear, appearDelay, hasAppeared, setHasAppeared, onAppeared, buttonRef, gsap, background, inner])

  return (
    <Component
      ref={buttonRef}
      className={`Button block relative select-none ${active ? 'active' : ''} focus-visible:outline ${style !== 'squaredark' && 'rounded-64 outline-scarlet outline-offset-2'} focus-visible:outline-2 group ${className ? className : ''}`}
      onClick={_onClick}
      disabled={disabled}
      type={type ? type : is === 'button' ? 'button' : undefined}
      aria-current={ariaCurrent}
      aria-label={ariaLabel}
      {...props}
    >
      {style === 'squaredark' ? (
        <span className='block size-48' />
      ) : (
        <span ref={background} className='Button__Background text-peach'>
          <svg
            className={`${size === 'large' ? 'size-80 md:size-52 lg:size-80' : 'size-48'}`}
            viewBox='0 0 80 80'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
          >
            <path
              d='M51.6472 2.00725L39.7494 0.262506C33.2511 -0.69127 26.6401 0.976557 21.3699 4.89807L11.7232 12.077C6.45565 15.9973 2.95975 21.852 2.00725 28.3502L0.262506 40.2481C-0.691271 46.7463 0.976557 53.3574 4.89807 58.6275L12.077 68.2742C15.9985 73.5431 21.852 77.039 28.3502 77.9927L40.2481 79.7375C46.7463 80.6913 53.3574 79.0234 58.6275 75.1019L68.2742 67.923C73.5431 64.0015 77.039 58.148 77.9928 51.6498L79.7375 39.7519C80.6913 33.2537 79.0235 26.6426 75.102 21.3725L67.923 11.7258C64.0015 6.45693 58.1481 2.96103 51.6498 2.00725H51.6472Z'
              fill='currentColor'
            />
          </svg>
        </span>
      )}
      <span
        aria-hidden={true}
        className={`Button__Inner absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex justify-center items-center w-full h-full`}
      >
        <span className='relative block overflow-hidden'>
          <UIIcon
            className='SquareButton__Icon text-16 relative group-hover:scale-110 transition-transform duration-333 ease-out-quad'
            icon={icon}
          />
        </span>
      </span>

      {!!label && (
        <span
          className={`SquareButton__Name pointer-events-none hidden md:block absolute ${classNameLabelPosition} transition-transform duration-333 ease-out-quart ${classNameLabel ? classNameLabel : ''}`}
        >
          <span className='text-body-s flex items-center justify-center px-16 rounded-16 min-h-40 bg-secondary'>
            {label}
          </span>
        </span>
      )}
    </Component>
  )
}
